import { ActionContext, ActionTree } from 'vuex'
import { AssignmentStateInterface } from 'src/store/assessment/state'
import { StoreInterface } from 'src/store'
import {
  getAssignments,
  getAssignmentAnswers,
  getUserAnswers,
  MUTATION_DISTRIBUTE_ASSIGNMENTS,
  MUTATION_SCORE_ASSIGNMENT,
  MUTATION_UPDATE_ASSIGNMENT_ANSWER,
  QUERY_GET_ASSIGNMENT_RECIPIENTS,
  QUERY_GET_SCORES_BY_USER,
  QUERY_GET_USER_ANSWERS,
  QUERY_GET_UNANSWERED_ASSIGNMENT_COUNT,
  MUTATION_INSERT_ASSIGNMENT_ATTACHMENT,
  MUTATION_DELETE_CONTENT_ASSET,
  MUTATION_DELETE_ASSET
} from 'src/lib/queries/assessment'
import { MutationOptions, QueryOptions } from 'apollo-client'
import {
  concatAssignmentAnswers,
  generateAssignmentObj
} from 'src/lib/queries/builder'
import { apolloProvider } from 'boot/apollo'
import { DefaultRole, GeneralStatusEnum } from 'able-lib'

function groupAssignmentAnswers (questions, answers) {
  const appendedQuestions = []
  questions.forEach((question) => {
    answers.forEach((answer) => {
      if (question.id === answer.assignment_id) {
        appendedQuestions.push({ ...question, user_content_id: answer.id, answer: answer.answer, status: answer.status, distributed_at: answer.created_at })
      }
    })
  })
  return appendedQuestions
}

const actions: ActionTree<AssignmentStateInterface, StoreInterface> = {
  async fetchAssignments ({ commit, rootState }: ActionContext<AssignmentStateInterface, StoreInterface>): Promise<any> {
    const isStudent = rootState.user.user.userRoles[0].role.name === DefaultRole.STUDENT
    const userId = rootState.user.user.id

    const { data } = await getAssignments(userId)
    if (isStudent) {
      const userAnswers = await getAssignmentAnswers(userId)
      const groupedData = groupAssignmentAnswers(data.assignments, userAnswers.data.assignment_answers)

      commit('setAssignments', groupedData)
    } else {
      const assignmentIds = data.assignments.map(d => d.id)
      const userAnswers = await getUserAnswers(assignmentIds)
      const concatedData = concatAssignmentAnswers(data.assignments, userAnswers.data.assignment_existing)

      commit('setTeacherAssignments', concatedData)
    }
  },

  async getUnansweredAssignmentsCount ({ commit }: ActionContext<AssignmentStateInterface, StoreInterface>, userId): Promise<any> {
    console.log('get unanswered assignments count')
    const options: QueryOptions = {
      query: QUERY_GET_UNANSWERED_ASSIGNMENT_COUNT,
      variables: {
        userId: userId
      },
      fetchPolicy: 'no-cache'
    }

    const { data } = await apolloProvider.defaultClient.query(options)
    commit('setUnansweredAssignmentsCount', data.unanswered_count.aggregate.count)
    console.log('count', data.unanswered_count.aggregate.count)
  },

  async answerAssignment ({ state, commit, dispatch, rootState }: ActionContext<AssignmentStateInterface, StoreInterface>, { answer, assets = [] }): Promise<any> {
    const options: MutationOptions = {
      mutation: MUTATION_UPDATE_ASSIGNMENT_ANSWER,
      variables: {
        id: answer.id,
        answer: answer.body,
        status: GeneralStatusEnum.PUBLISHED,
        datetimenow: '2020-07-07'
      }
    }
    await apolloProvider.defaultClient.mutate(options)

    assets.forEach(asset => {
      if (!asset.id) {
        const assetOption: MutationOptions = {
          mutation: MUTATION_INSERT_ASSIGNMENT_ATTACHMENT,
          variables: {
            id: answer.id,
            url: asset.url,
            description: asset.description,
            class: asset.class,
            type: asset.type,
            is_shared: asset.is_shared
          }
        }
        apolloProvider.defaultClient.mutate(assetOption)
      }
    })

    const answeredIndex = state.assignments.findIndex(data => data.answer.id === answer.id)
    commit('setAnswer', {
      index: answeredIndex,
      value: answer
    })

    dispatch('fetchAssignments')
    dispatch('getUnansweredAssignmentsCount', rootState.user.user.id)
  },

  async scoreAssignment ({ dispatch }: ActionContext<AssignmentStateInterface, StoreInterface>, { contextId, feedback, score, userId }): Promise<any> {
    const options: MutationOptions = {
      mutation: MUTATION_SCORE_ASSIGNMENT,
      variables: {
        contextId: contextId,
        feedback: feedback,
        score: score,
        status: GeneralStatusEnum.ACKNOWLEDGED,
        userId: userId
      }
    }
    await apolloProvider.defaultClient.mutate(options)
    dispatch('fetchAssignments')
    // TODO refresh assignment
  },

  async fetchScores ({ commit, rootState }: ActionContext<AssignmentStateInterface, any>): Promise<any> {
    const user_selector = []
    if (rootState.user.user.userRoles[0].role.name === 'STUDENT') {
      user_selector.push(rootState.user.user.id)
    } else {
      rootState.assessment.studentResponses.map(data => data.answer).map((d) => {
        d.map((t) => {
          user_selector.push(t.user.id)
        })
      })
    }

    const options: QueryOptions = {
      query: QUERY_GET_SCORES_BY_USER,
      variables: {
        users: user_selector
      },
      fetchPolicy: 'no-cache'
    }
    const { data } = await apolloProvider.defaultClient.query(options)
    commit('setScores', data.scores)
  },

  async generateAssignment ({ rootState }: ActionContext<AssignmentStateInterface, StoreInterface>, assignmentId): Promise<any> {
    const assignments_options: QueryOptions = {
      query: QUERY_GET_ASSIGNMENT_RECIPIENTS,
      fetchPolicy: 'no-cache',
      variables: {
        userId: rootState.user.user.id,
        assignmentId: assignmentId
      }
    }
    const { data } = await apolloProvider.defaultClient.query(assignments_options)

    const assignmentIds = data.assignment_array.map(data => data.id)

    const assignment_answers_options: QueryOptions = {
      query: QUERY_GET_USER_ANSWERS,
      fetchPolicy: 'no-cache',
      variables: {
        assignmentIds: assignmentIds
      }
    }
    const distributedAssignments = await apolloProvider.defaultClient.query(assignment_answers_options)

    if (data.assignment_array !== undefined) {
      const receivedUsers = distributedAssignments.data.assignment_existing.map(assignment => assignment.user.id)
      const expectedRecipientIds = data.assignment_array[0].users_expected.users.map(user => user.id)
      const recipientIds = expectedRecipientIds.filter(expectedRecipientId => !receivedUsers.includes(expectedRecipientId))

      const assignmentsToInsert = generateAssignmentObj(assignmentId, recipientIds)

      const mutate_options: MutationOptions = {
        mutation: MUTATION_DISTRIBUTE_ASSIGNMENTS,
        variables: {
          objects:
            assignmentsToInsert
        },
        fetchPolicy: 'no-cache'
      }
      await apolloProvider.defaultClient.mutate(mutate_options)
    }
  },

  async deleteAttachment ({ commit }: ActionContext<AssignmentStateInterface, StoreInterface>, { attachItem, assignmentID, index }): Promise<any> {
    const options: MutationOptions = {
      mutation: MUTATION_DELETE_CONTENT_ASSET,
      variables: {
        contentId: assignmentID,
        assetId: attachItem.id
      }
    }
    await apolloProvider.defaultClient.mutate(options)

    const assetOptions: MutationOptions = {
      mutation: MUTATION_DELETE_ASSET,
      variables: {
        id: attachItem.id
      }
    }
    await apolloProvider.defaultClient.mutate(assetOptions)

    commit('removeAttachment', index)
  }
}

export default actions
